import { UnroutedAssignmentRefusalResponse } from '@chiroup/core/types/ConnectorAdmin.type';

type ConnectorError = {
  response?: { data?: unknown };
  message?: string;
};

const asConnectorError = (error: unknown): ConnectorError | null =>
  error && typeof error === 'object' ? (error as ConnectorError) : null;

/**
 * ONE message extraction for connector admin surfaces (INFRA-10.2 item 3):
 * server refusals arrive as `response.data.message` (typed BadRequestErrors
 * through the endpoint wrapper), plain failures as `message`. The ops-console
 * retry display and the queue mutations render through this same helper so a
 * refusal reads identically wherever it surfaces.
 */
export const connectorErrorMessage = (error: unknown, fallback: string): string => {
  const e = asConnectorError(error);
  const data = e?.response?.data;
  const responseMessage =
    data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
      ? data.message
      : undefined;

  return responseMessage || e?.message || fallback;
};

/** Structured assignment refusal returned only by the Unrouted assign endpoint. */
export const connectorAssignmentRefusal = (
  error: unknown
): UnroutedAssignmentRefusalResponse | null => {
  const data = asConnectorError(error)?.response?.data;

  if (!data || typeof data !== 'object' || !('message' in data) || !('details' in data)) {
    return null;
  }

  const details = data.details;

  if (
    typeof data.message !== 'string' ||
    !details ||
    typeof details !== 'object' ||
    !('ts' in details) ||
    typeof details.ts !== 'number' ||
    !('by' in details) ||
    typeof details.by !== 'string' ||
    !('reason' in details) ||
    typeof details.reason !== 'string' ||
    !('diagnostics' in details) ||
    !details.diagnostics ||
    typeof details.diagnostics !== 'object'
  ) {
    return null;
  }

  return data as UnroutedAssignmentRefusalResponse;
};
